import { useRef, useCallback, useState, type ReactNode } from "react";
import { Virtualizer, VirtualizerHandle } from "virtua";
import { cn } from "../lib/utils";

export interface PaginatedListProps {
  children: ReactNode;
  onBottomReached?: () => void;
  onTopReached?: () => void;
  className?: string;
  /**
   * Threshold in pixels from the edge to trigger callbacks.
   * Defaults to 50px from top/bottom.
   */
  threshold?: number;
  /**
   * When true, the scrollbar is hidden until the user actively scrolls.
   * Defaults to false.
   */
  hideScrollbarUntilScroll?: boolean;
}

/**
 * A virtualized scrollable container component that detects when the user has scrolled to the top or bottom.
 * Uses virtua for performant virtualization and scroll detection.
 *
 * @example
 * ```tsx
 * <PaginatedList
 *   onBottomReached={() => loadMoreItems()}
 *   onTopReached={() => loadPreviousItems()}
 *   className="h-96"
 * >
 *   {items.map(item => <Item key={item.id} {...item} />)}
 * </PaginatedList>
 * ```
 */
export function PaginatedList({
  children,
  onBottomReached,
  onTopReached,
  className,
  threshold = 50,
  hideScrollbarUntilScroll = false,
}: PaginatedListProps) {
  const virtualizerRef = useRef<VirtualizerHandle>(null);
  const topReachedRef = useRef<boolean>(false);
  const bottomReachedRef = useRef<boolean>(false);
  const scrollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isScrolling, setIsScrolling] = useState(false);

  const handleTopReached = useCallback(() => {
    if (!topReachedRef.current && onTopReached) {
      topReachedRef.current = true;
      bottomReachedRef.current = false;
      onTopReached();
    }
  }, [onTopReached]);

  const handleBottomReached = useCallback(() => {
    if (!bottomReachedRef.current && onBottomReached) {
      bottomReachedRef.current = true;
      topReachedRef.current = false;
      onBottomReached();
    }
  }, [onBottomReached]);

  const handleScroll = useCallback(
    (offset: number) => {
      const virtualizer = virtualizerRef.current;
      if (!virtualizer) return;

      const { scrollSize, viewportSize } = virtualizer;

      // Detect scrolled to top
      if (offset <= threshold) {
        handleTopReached();
      } else {
        // Reset flag when scrolled away from top
        topReachedRef.current = false;
      }

      // Detect scrolled to bottom
      if (offset - scrollSize + viewportSize >= -threshold) {
        handleBottomReached();
      } else {
        // Reset flag when scrolled away from bottom
        bottomReachedRef.current = false;
      }

      if (hideScrollbarUntilScroll) {
        setIsScrolling(true);
        if (scrollingTimeoutRef.current) clearTimeout(scrollingTimeoutRef.current);
        scrollingTimeoutRef.current = setTimeout(() => setIsScrolling(false), 1000);
      }
    },
    [threshold, handleTopReached, handleBottomReached, hideScrollbarUntilScroll]
  );

  return (
    <div
      className={cn(
        "overflow-y-auto",
        hideScrollbarUntilScroll && [
          "scrollbar-thin scrollbar-track-transparent",
          isScrolling ? "scrollbar-thumb-black/50" : "scrollbar-thumb-transparent",
        ],
        className
      )}
    >
      <Virtualizer ref={virtualizerRef} onScroll={handleScroll}>
        {children}
      </Virtualizer>
    </div>
  );
}
